/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../theme/theme"
import { css } from "@emotion/react"
import { Box, Typography, CircularProgress, Alert } from "@mui/material"
import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { mobileCss } from "../../util/isMobile"
import { useIsMobile } from "../../util/useIsMobile"
import { EditorFormProvider, useEditorForm } from "./context/EditorFormContext"
import { EditorFormContextBridge } from "./context/EditorFormContextBridge"
import { EditorSidebar } from "./components/EditorSidebar"
import { EditorSaveButton } from "./components/EditorSaveButton"
import { getEditorSteps } from "../common/constants/formSteps"
import { fetchQuestionnaireFromGitHub, decodeBranchName } from "../../../services/githubFetchService"
import { initialFormData } from "../common/context/SharedFormTypes"

const EditorStyle = withMyTheme((theme) => css`
    background-color: white;
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
`)

const HeaderStyle = withMyTheme((theme) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid ${theme.palette.divider};
    background-color: white;
    flex-shrink: 0;
    
    ${mobileCss(`
        padding: 1rem;
        flex-wrap: wrap;
        gap: 1rem;
    `)}
`)

const TitleStyle = withMyTheme((theme) => css`
    font-size: 1.8rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    
    ${mobileCss(`
        font-size: 1.3rem;
    `)}
`)

const MainContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex: 1;
    overflow: hidden;
    
    ${mobileCss(`
        flex-direction: column;
    `)}
`)

const StepContentAreaStyle = withMyTheme((theme) => css`
    flex: 1;
    overflow-y: auto;
    padding: 2rem;
    
    ${mobileCss(`
        padding: 1rem;
    `)}
`)

const CoupleNamesStyle = withMyTheme((theme) => css`
    font-family: ${theme.typography.body1.fontFamily};
    font-size: 1.5rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    
    ${mobileCss(`
        font-size: 0.95rem;
        order: 3;
        width: 100%;
        text-align: center;
    `)}
`)

const LoadingContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    gap: 1.5rem;
`)

const STORAGE_KEY = 'editor-form-data'

const EditorContent = () => {
    const { t } = useTranslation()
    const { formData, loadFormData, storageContext, hasUnsavedChanges, saveForm } = useEditorForm()
    const { branchNameBase64 } = useParams<{ branchNameBase64?: string }>()
    const [selectedStepId, setSelectedStepId] = useState<string>('basic-info')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [currentBranchName, setCurrentBranchName] = useState<string>('')
    const isMobile = useIsMobile()
    const hasLoadedRef = useRef(false)

    const editorSteps = getEditorSteps()

    // Fetch data from GitHub if branchNameBase64 is provided
    useEffect(() => {
        const fetchDataFromGitHub = async () => {
            // Prevent multiple fetches
            if (hasLoadedRef.current) {
                return
            }

            if (!branchNameBase64) {
                setError('No branch name provided in URL')
                return
            }

            // Check if there's existing localStorage data
            const savedData = localStorage.getItem(STORAGE_KEY)
            if (savedData && savedData !== JSON.stringify(initialFormData)) {
                console.log('Editor localStorage data exists, skipping GitHub fetch')
                hasLoadedRef.current = true
                return
            }

            setLoading(true)
            setError(null)

            try {
                const branchName = decodeBranchName(branchNameBase64)
                setCurrentBranchName(branchName)
                console.log(`Editor: Fetching data for branch: ${branchName}`)

                const data = await fetchQuestionnaireFromGitHub(branchName, storageContext)
                loadFormData(data)
                hasLoadedRef.current = true

                console.log('Editor: Successfully loaded data from GitHub')
            } catch (err) {
                console.error('Editor: Failed to fetch data:', err)
                setError(err instanceof Error ? err.message : 'Failed to load editor data')
            } finally {
                setLoading(false)
            }
        }

        fetchDataFromGitHub()
    }, [branchNameBase64, loadFormData, storageContext])

    // Decode branch name on mount if available
    useEffect(() => {
        if (branchNameBase64 && !currentBranchName) {
            try {
                const branchName = decodeBranchName(branchNameBase64)
                setCurrentBranchName(branchName)
            } catch (err) {
                console.error('Failed to decode branch name:', err)
            }
        }
    }, [branchNameBase64, currentBranchName])

    const handleStepSelect = (stepId: string) => {
        setSelectedStepId(stepId)
    }

    const handleSave = async () => {
        if (!currentBranchName) {
            throw new Error('No branch name available for saving')
        }
        await saveForm(currentBranchName)
    }

    const selectedStep = editorSteps.find(step => step.id === selectedStepId)
    const StepComponent = selectedStep?.component

    if (loading) {
        return (
            <Box css={LoadingContainerStyle}>
                <CircularProgress size={60} />
                <Typography variant="h6">{t('editor.loading')}</Typography>
            </Box>
        )
    }

    if (error) {
        return (
            <Box css={LoadingContainerStyle}>
                <Alert severity="error" sx={{ maxWidth: '600px' }}>
                    {error}
                </Alert>
            </Box>
        )
    }

    return (
        <>
            <Box css={HeaderStyle}>
                <Typography css={TitleStyle}>
                    {t('editor.title')}
                </Typography>
                {(formData.basicInfo.coupleName1 || formData.basicInfo.coupleName2) && (
                    <Typography
                        css={CoupleNamesStyle}
                        style={{ color: formData.colors?.primaryColor || undefined }}
                    >
                        {[formData.basicInfo.coupleName1, formData.basicInfo.coupleName2].filter(Boolean).join(' & ')}
                    </Typography>
                )}
                <EditorSaveButton 
                    hasUnsavedChanges={true}
                    onSave={handleSave}
                />
            </Box>

            <Box css={MainContentStyle}>
                {!isMobile && (
                    <EditorSidebar
                        steps={editorSteps}
                        selectedStepId={selectedStepId}
                        onStepSelect={handleStepSelect}
                    />
                )}
                
                <Box css={StepContentAreaStyle}>
                    {isMobile && (
                        <EditorSidebar
                            steps={editorSteps}
                            selectedStepId={selectedStepId}
                            onStepSelect={handleStepSelect}
                        />
                    )}
                    
                    {StepComponent && <StepComponent />}
                </Box>
            </Box>
        </>
    )
}

export const Editor = () => {
    return (
        <div css={EditorStyle}>
            <EditorFormProvider>
                <EditorFormContextBridge>
                    <EditorContent />
                </EditorFormContextBridge>
            </EditorFormProvider>
        </div>
    )
}
